export const retrievePaywallCheckoutHostname = () =>
  `${window.location.origin}/checkout/`;

const buildPaywallCheckoutUrl = (
  checkoutPath: string,
  priceId?: number,
): string => {
  const checkoutUrl = new URL(
    `${retrievePaywallCheckoutHostname()}${checkoutPath}`,
  );

  if (priceId) {
    checkoutUrl.searchParams.set("price_id", String(priceId));
  }

  return checkoutUrl.toString();
};

export const copyPaywallCheckoutUrlWithPrefix = (
  checkoutPath: string,
  clipboardFallbackSelector: string,
  priceId?: number,
): void => {
  const checkoutUrl = buildPaywallCheckoutUrl(checkoutPath, priceId);

  if (navigator?.clipboard) {
    void navigator.clipboard.writeText(checkoutUrl);
  } else {
    // clipboard fallback
    const tempInput = document.createElement("input");

    tempInput.style.position = "absolute";
    tempInput.style.left = "-1000px";
    tempInput.style.top = "-1000px";
    tempInput.value = checkoutUrl;

    const fallbackElement = document.querySelector(clipboardFallbackSelector);
    if (fallbackElement) {
      fallbackElement.appendChild(tempInput);
    }

    tempInput.focus();
    tempInput.setSelectionRange(0, tempInput.value.length);

    document.execCommand("copy");
  }
};

export const PaymentMethodType = {
  CARD: "card",
  PIX: "pix",
  GOOGLE_PAY: "google_pay",
  APPLE_PAY: "apple_pay",
  LINK: "link",
  KLARNA: "klarna",
  AFTERPAY_CLEARPAY: "afterpay_clearpay",
  AFFIRM: "affirm",
  // EU Payment Methods
  SEPA_DEBIT: "sepa_debit",
  IDEAL: "ideal",
  BANCONTACT: "bancontact",
} as const;

const PAYMENT_METHOD_VALUES: readonly string[] = [
  PaymentMethodType.CARD,
  PaymentMethodType.PIX,
  PaymentMethodType.GOOGLE_PAY,
  PaymentMethodType.APPLE_PAY,
  PaymentMethodType.LINK,
  PaymentMethodType.KLARNA,
  PaymentMethodType.AFTERPAY_CLEARPAY,
  PaymentMethodType.AFFIRM,
  // EU Payment Methods
  PaymentMethodType.SEPA_DEBIT,
  PaymentMethodType.IDEAL,
  PaymentMethodType.BANCONTACT,
];

const isValidPaymentMethodType = (
  paymentMethodType: string,
): paymentMethodType is (typeof PaymentMethodType)[keyof typeof PaymentMethodType] =>
  PAYMENT_METHOD_VALUES.includes(paymentMethodType);

const normalizePaymentMethodType = (
  paymentMethodType: string,
): PaymentMethodType | null => {
  const normalized = paymentMethodType.toLowerCase();
  return isValidPaymentMethodType(normalized) ? normalized : null;
};

const isPaymentMethod = (
  paymentMethodType: string,
  method: PaymentMethodType,
) => normalizePaymentMethodType(paymentMethodType) === method;

type PaymentMethodType =
  (typeof PaymentMethodType)[keyof typeof PaymentMethodType];
export const isPix = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.PIX);
export const isCard = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.CARD);

export const isLink = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.LINK);
const isGooglePay = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.GOOGLE_PAY);
const isApplePay = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.APPLE_PAY);
export const isKlarna = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.KLARNA);
export const isAfterpayClearpay = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.AFTERPAY_CLEARPAY);
const isAffirm = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.AFFIRM);

export const isDigitalWallet = (paymentMethodType: string) =>
  isGooglePay(paymentMethodType) || isApplePay(paymentMethodType);

export const isBuyNowPayLater = (paymentMethodType: string) =>
  isKlarna(paymentMethodType) ||
  isAfterpayClearpay(paymentMethodType) ||
  isAffirm(paymentMethodType);

export const isSepaDebit = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.SEPA_DEBIT);

const isIdeal = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.IDEAL);

const isBancontact = (paymentMethodType: string) =>
  isPaymentMethod(paymentMethodType, PaymentMethodType.BANCONTACT);

export const cannotPurchaseAgain = (
  hasAlreadyPurchased: boolean,
  paywall: any,
) => hasAlreadyPurchased && !paywall?.can_be_repurchased;

// TODO: resolve payment method types that support off-session usage from the backend
export const isOffSessionUsageSupported = (paymentMethodType: string) =>
  isApplePay(paymentMethodType) ||
  isBancontact(paymentMethodType) ||
  isCard(paymentMethodType) ||
  isGooglePay(paymentMethodType) ||
  isIdeal(paymentMethodType) ||
  isKlarna(paymentMethodType) ||
  isLink(paymentMethodType) ||
  isSepaDebit(paymentMethodType);
